import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { ASSETS, NAV_LINKS, X_URL } from "@/data/content";

export const XMark = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-ice/10 bg-abyss/80 backdrop-blur-xl shadow-[0_0_40px_rgba(22,119,255,0.12)]"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 md:h-[72px] md:px-8">
          <Link to="/" data-testid="nav-logo-link" className="group flex items-center gap-3">
            <img
              src={ASSETS.logo}
              alt="ARCIMUS logo"
              className="h-9 w-9 rounded-full object-cover ring-1 ring-ice/30 transition-shadow duration-300 group-hover:shadow-[0_0_18px_rgba(0,168,255,0.6)] md:h-10 md:w-10"
            />
            <div className="leading-none">
              <span className="font-display text-sm font-bold tracking-[0.28em] text-chrome md:text-base">
                ARCIMUS
              </span>
              <span className="mt-1 hidden font-term text-[9px] tracking-[0.3em] text-silver sm:block">
                PRIME-001
              </span>
            </div>
          </Link>

          <nav className="hidden items-center gap-6 xl:gap-7 min-[1180px]:flex" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/"}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative font-term text-[11px] tracking-[0.24em] transition-colors duration-300 ${
                    isActive ? "text-ice text-glow-blue" : "text-silver hover:text-chrome"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute -bottom-2 left-0 h-px bg-electric shadow-[0_0_10px_rgba(0,168,255,0.9)] transition-[width] duration-300 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={X_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="ARCIMUS on X"
              data-testid="nav-x-link"
              className="hidden h-9 w-9 items-center justify-center border border-ice/20 text-silver transition-colors duration-300 hover:border-electric hover:text-ice hover:shadow-[0_0_16px_rgba(0,168,255,0.4)] min-[1180px]:flex"
            >
              <XMark className="h-3.5 w-3.5" />
            </a>
            <Link
              to="/terminal"
              data-testid="nav-enter-terminal-btn"
              className="clip-angular-sm group hidden items-center gap-2 bg-arc px-4 py-2.5 font-term text-[11px] font-semibold tracking-[0.22em] text-white transition-shadow duration-300 hover:shadow-[0_0_28px_rgba(22,119,255,0.7)] min-[1180px]:flex"
            >
              ENTER TERMINAL
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              type="button"
              aria-label="Open command menu"
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="flex h-10 w-10 items-center justify-center border border-ice/20 text-chrome min-[1180px]:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-command-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-abyss/95 backdrop-blur-2xl"
          >
            <div className="grid-overlay pointer-events-none absolute inset-0 opacity-60" />
            <div className="relative flex h-16 items-center justify-between px-5">
              <span className="font-display text-sm font-bold tracking-[0.3em] text-chrome">
                ARCIMUS SYSTEM
              </span>
              <button
                type="button"
                aria-label="Close command menu"
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center border border-ice/20 text-chrome"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="relative flex flex-1 flex-col justify-center gap-1 px-8" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.4 }}
                >
                  <NavLink
                    to={link.path}
                    end={link.path === "/"}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `group flex items-baseline gap-4 border-b border-ice/10 py-4 ${
                        isActive ? "text-ice" : "text-chrome"
                      }`
                    }
                  >
                    <span className="font-term text-[10px] tracking-[0.3em] text-electric">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-2xl font-bold tracking-[0.18em] transition-colors duration-300 group-hover:text-ice">
                      {link.label}
                    </span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="relative flex items-center justify-between px-8 pb-10">
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-x-link"
                className="flex items-center gap-2 font-term text-[11px] tracking-[0.3em] text-silver"
              >
                <XMark className="h-4 w-4" /> X / ARCIMUS
              </a>
              <span className="hud-label">SYS // ONLINE</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
